import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Download, Loader2, Send } from "lucide-react";
import { API_BASE } from "../lib/api";
import { EASE, TriangleMark } from "./Weave";

export function WaitlistForm({ onDone, prefix = "waitlist" }) {
  const [form, setForm] = useState({ name: "", email: "", permission: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.permission) {
      setError("Please tick the permission box so we can email you the prospectus.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      const { data } = await axios.post(`${API_BASE}/waitlist`, form);
      setResult(data);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail || "Something went wrong — please try again in a moment.");
    }
  };

  if (status === "success") {
    const firstName = form.name.trim().split(" ")[0];
    return (
      <motion.div
        data-testid={`${prefix}-success-message`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative overflow-hidden rounded-2xl border border-tealb/40 bg-teallight p-6 text-center sm:p-8"
      >
        <TriangleMark className="pointer-events-none absolute -right-6 -top-6 h-20 opacity-20" />
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-tealb shadow-[0_12px_30px_-10px_rgba(74,160,164,0.7)]">
          <Check className="h-6 w-6 text-white" strokeWidth={3} />
        </span>
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-tealdark">
          Welcome{firstName ? `, ${firstName}` : ""} — you&rsquo;re on the list
        </p>
        <p className="mt-3 text-xl font-extrabold leading-snug tracking-tight text-ink sm:text-2xl">
          You&rsquo;ve made the most important first step to reinvention.
        </p>
        <p className="mt-3 leading-relaxed text-body2">
          Your Personal Clarity prospectus is on its way to{" "}
          <strong className="font-semibold text-ink">{form.email}</strong> — give it a minute or
          two, and check your spam folder just in case.
        </p>
        {result?.email_status !== "sent" && (
          <a
            data-testid={`${prefix}-download-prospectus-link`}
            href={`${API_BASE}/prospectus`}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-tealdark underline underline-offset-4 hover:text-ink"
          >
            <Download className="h-4 w-4" />
            Download the prospectus now
          </a>
        )}
        <p className="mt-6 text-sm leading-relaxed text-body2">
          Any questions? We&rsquo;d love to hear from you.{" "}
          <a
            href="mailto:hello@wearetheweave.co.uk"
            data-testid={`${prefix}-contact-email-link`}
            className="font-semibold text-ink underline underline-offset-4 hover:text-wred"
          >
            hello@wearetheweave.co.uk
          </a>
        </p>
        {onDone && (
          <button
            data-testid={`${prefix}-success-done-button`}
            onClick={onDone}
            className="mt-7 block w-full rounded-xl bg-ink py-3.5 text-base font-semibold text-paper transition-colors hover:bg-wred"
          >
            Done
          </button>
        )}
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <div>
        <label htmlFor="wl-name" className="mb-2 block text-sm font-semibold text-ink">
          Full name
        </label>
        <input
          id="wl-name"
          data-testid={`${prefix}-input-name`}
          type="text"
          required
          autoComplete="name"
          placeholder="e.g. James Cracknell"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full rounded-xl border border-paperborder bg-white px-4 py-3.5 text-base text-ink outline-none transition placeholder:text-muted2/70 focus:border-tealb focus:ring-2 focus:ring-tealb/40"
        />
      </div>

      <div>
        <label htmlFor="wl-email" className="mb-2 block text-sm font-semibold text-ink">
          Email
        </label>
        <input
          id="wl-email"
          data-testid={`${prefix}-input-email`}
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full rounded-xl border border-paperborder bg-white px-4 py-3.5 text-base text-ink outline-none transition placeholder:text-muted2/70 focus:border-tealb focus:ring-2 focus:ring-tealb/40"
        />
      </div>

      <label className="flex cursor-pointer select-none items-start gap-3">
        <input
          data-testid={`${prefix}-checkbox-terms`}
          type="checkbox"
          checked={form.permission}
          onChange={(e) => setForm({ ...form, permission: e.target.checked })}
          className="peer sr-only"
          required
        />
        <span
          data-testid={`${prefix}-checkbox-visual`}
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 border-paperborder bg-white transition-colors peer-checked:border-tealb peer-checked:bg-tealb peer-focus-visible:ring-2 peer-focus-visible:ring-tealb/40"
        >
          <Check className={`h-3.5 w-3.5 text-white transition-opacity ${form.permission ? "opacity-100" : "opacity-0"}`} strokeWidth={3.5} />
        </span>
        <span className="text-sm leading-relaxed text-body2">
          We have permission to contact you about the Academy — send me the prospectus.
        </span>
      </label>

      {error && (
        <p data-testid="waitlist-error-message" className="rounded-xl bg-redlight px-4 py-3 text-sm font-medium text-wred">
          {error}
        </p>
      )}

      <button
        data-testid={`${prefix}-submit-button`}
        type="submit"
        disabled={status === "sending"}
        className="group inline-flex w-full items-center justify-center gap-3 rounded-xl bg-wred py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            Send me the prospectus
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
          </>
        )}
      </button>

      <p className="text-center text-xs leading-relaxed text-muted2">
        We&rsquo;ll only ever email you about the Academy — no spam, ever. Unsubscribe any time.
      </p>
    </form>
  );
}

export function WaitlistModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-lenis-prevent
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto px-4 py-6 sm:py-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <motion.div
        data-testid="waitlist-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Join the waitlist"
        initial={{ opacity: 0, y: 36, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="relative w-full max-w-lg rounded-3xl border border-paperborder bg-paper p-6 shadow-2xl sm:p-10"
      >
        <TriangleMark className="pointer-events-none absolute -right-5 -top-5 h-14 opacity-95" />
        <button
          data-testid="waitlist-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-paperborder bg-white text-ink transition-colors hover:bg-ink hover:text-paper"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-wred">Join the waitlist</p>
        <h3 className="mt-2 text-2xl font-bold tracking-tight text-ink sm:text-3xl">
          Your prospectus, straight to your inbox
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-body2 sm:text-base">
          Personal Clarity starts April 2027. Tell us where to send it — the full prospectus arrives as
          a PDF the moment you&rsquo;re on the list.
        </p>

        <div className="mt-7">
          <WaitlistForm onDone={onClose} prefix="modal" />
        </div>
      </motion.div>
    </motion.div>
  );
}

export function FinalCta() {
  return (
    <section id="waitlist" data-testid="waitlist-form-container" className="relative scroll-mt-24 overflow-hidden bg-wyellow">
      <img
        src="/triangles.webp"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-1/2 h-[80%] w-auto -translate-y-1/2 opacity-25 mix-blend-multiply"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-12 lg:px-8 lg:py-28">
        <div className="lg:col-span-6">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/70">April 2027 · Waitlist open</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Personal Clarity: let&rsquo;s begin.
          </h2>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink/80 sm:text-xl">
            You do not need to know you&rsquo;re a founder. You just need to be curious:{" "}
            <em className="font-semibold">could I be one?</em>
          </p>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-ink/70">
            Join the waitlist and we&rsquo;ll email you the full Personal Clarity prospectus — ten
            weeks, four stages, and an honest answer either way.
          </p>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-3xl border border-ink/10 bg-paper p-6 shadow-[0_30px_60px_-30px_rgba(14,47,50,0.5)] sm:p-9">
            <WaitlistForm prefix="cta" />
          </div>
        </div>
      </div>
    </section>
  );
}
